import { memo, useMemo } from "react";
import { Marker, OverlayView } from "@react-google-maps/api";
import { IoPricetag } from "react-icons/io5";
import { CurrentViewTypeEnum } from "./typings";
import { IconOSNew, IconRepresentaDias } from "../../MapInfoSemaforo/IconsMap";
import { getMatchedWindowsForPlanos } from "../../FiltroAcompanhamentoOperacional/utils";

let zeroIconSize: google.maps.Size | undefined;

function getZeroIconSize() {
  if (!zeroIconSize) {
    zeroIconSize = new google.maps.Size(0, 0);
  }
  return zeroIconSize;
}

const getBadgeOffset = () => ({ x: 18, y: -18 });

function getPlanoTalhaoDisponiveis(
  planosoperacionais: any[] | undefined,
  rangeDatesDeleted: any,
  possuiOSAberta: boolean = false
) {
  if (!planosoperacionais) return [];

  return planosoperacionais.filter((p: any) => {
    const naoDeletado = !rangeDatesDeleted?.some(
      (f: any) => f.dateOriginal === p.DataProgramada
    );
    if (!naoDeletado) return false;
    if (p.PossuiOSAberta === true && p.PossuiOSAberta === possuiOSAberta) {
      return false;
    }
    return true;
  });
}

export interface ExecucaoTalhaoMarkerProps {
  item: any;
  currentViewType: CurrentViewTypeEnum;
  displayMarkers: boolean;
  heavyOverlaysEnabled: boolean;
  menuName?: string;
  janelaExecucao?: boolean;
  rangeDatesDeleted: any;
  semOS?: boolean;
  rangeWindows: any;
  numberRepeted: any;
  isLoading?: boolean;
  ordemServico?: boolean;
  justificativaAtraso?: boolean;
  statusSemaforo?: {
    atrasado?: boolean;
    proximo?: boolean;
    emDia?: boolean;
  };
  indicadoresDeDia?: Set<string> | null;
  zoomScale: number;
}

function ExecucaoTalhaoMarkerComponent({
  item,
  currentViewType,
  displayMarkers,
  heavyOverlaysEnabled,
  menuName,
  janelaExecucao,
  rangeDatesDeleted,
  semOS,
  rangeWindows,
  numberRepeted,
  isLoading,
  ordemServico,
  justificativaAtraso,
  statusSemaforo,
  indicadoresDeDia,
  zoomScale,
}: ExecucaoTalhaoMarkerProps) {
  const lat = item.properties?.pontoCentralLat;
  const lng = item.properties?.pontoCentralLong;
  const codigo = item.properties?.codigo;
  const isBox = currentViewType === CurrentViewTypeEnum.box;
  const showMarker = currentViewType !== CurrentViewTypeEnum.transparent && displayMarkers;

  const label = useMemo(
    () => ({
      text: codigo,
      color: isBox ? "#000" : "#fff",
      fontWeight: isBox ? "regular" : "bold",
    }),
    [codigo, isBox]
  );

  const icon = useMemo(
    () => ({
      url: "/marker.png",
      size: isBox ? undefined : getZeroIconSize(),
    }),
    [isBox]
  );

  const position = useMemo(
    () => ({
      lat,
      lng,
    }),
    [lat, lng]
  );

  const windowExecutionCount = useMemo(() => {
    if (menuName !== "Mapa Cronograma" || !janelaExecucao) return 0;
    const planosTalhao = getPlanoTalhaoDisponiveis(
      item.properties?.planosoperacionais,
      rangeDatesDeleted,
      semOS
    );
    return getMatchedWindowsForPlanos(planosTalhao, rangeWindows).length;
  }, [
    menuName,
    janelaExecucao,
    item.properties?.planosoperacionais,
    rangeDatesDeleted,
    semOS,
    rangeWindows,
  ]);

  const operacoesBadgeCount = useMemo(() => {
    if (!heavyOverlaysEnabled || !displayMarkers || janelaExecucao) return 0;
    if (menuName !== "Mapa Cronograma" || isLoading) return 0;

    const operacoesQtde = item.properties?.planosoperacionais?.length ?? 0;
    if (operacoesQtde <= 1) return 0;

    const planoTalhaoDisponiveis = getPlanoTalhaoDisponiveis(
      item.properties?.planosoperacionais,
      rangeDatesDeleted,
      semOS
    );
    if (planoTalhaoDisponiveis?.length <= 1) return 0;

    if (
      numberRepeted?.length > 0 &&
      !numberRepeted.includes(item.properties?.codigo)
    ) {
      return 0;
    }

    const dataProgramada = item.properties?.planosoperacionais?.flatMap(
      (p: any) => p.DataProgramada
    );
    if (
      rangeDatesDeleted?.find(
        (f: any) =>
          f?.id === item.properties?.id &&
          dataProgramada?.includes(f.dateOriginal)
      )
    ) {
      return 0;
    }

    return planoTalhaoDisponiveis.length;
  }, [
    heavyOverlaysEnabled,
    displayMarkers,
    janelaExecucao,
    menuName,
    isLoading,
    item.properties?.planosoperacionais,
    item.properties?.codigo,
    item.properties?.id,
    rangeDatesDeleted,
    semOS,
    numberRepeted,
  ]);

  const possuiOSAberta = item.properties?.planosoperacionais?.every(
    (p: any) => p.PossuiOSAberta
  );
  const listaAtrasadas = item.properties?.planosoperacionais?.filter(
    (p: any) => p.StatusSemaforo === 3
  );
  const possuiJustificativa =
    listaAtrasadas?.length > 0
      ? listaAtrasadas?.every((p: any) => p.PendenteJustificativa === false)
      : false;

  const findedProximoVencimento = item.properties?.planosoperacionais?.find(
    (plano: any) => plano.QuantidadeDias >= 0 && plano.QuantidadeDias <= 3 && plano.StatusSemaforo === 2
  );

  const findedAtrasado = item.properties?.planosoperacionais
    ?.filter((item: any) => item.StatusSemaforo === 3)
    ?.reduce(
    (menor: any, atual: any) => {
      if (atual.QuantidadeDias <= -1) {
        if (!menor || atual.QuantidadeDias < menor.QuantidadeDias) {
          return atual;
        }
      }
      return menor;
    },
    null
  );

  const fillColor = item.properties?.fill;
  const isProximoVencimento = fillColor === "#FFA500";
  const isExecutada = findedAtrasado && !isProximoVencimento ? findedAtrasado?.DataExecucao !== null : findedProximoVencimento?.DataExecucao !== null;

  const showSemaforoOverlays =
    heavyOverlaysEnabled &&
    displayMarkers &&
    menuName === "Mapa Semáforo" &&
    lat != null &&
    lng != null;

  return (
    <>
      <Marker
        visible={showMarker}
        clickable={false}
        label={label}
        icon={icon}
        position={position}
      />

      {heavyOverlaysEnabled &&
        menuName === "Mapa Cronograma" &&
        janelaExecucao &&
        displayMarkers &&
        windowExecutionCount > 1 &&
        lat != null &&
        lng != null && (
          <OverlayView
            position={position}
            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            getPixelPositionOffset={getBadgeOffset}
          >
            <div
              style={{
                width: 20,
                height: 20,
                borderRadius: "50%",
                background: "#D32F2F",
                color: "#fff",
                fontSize: 11,
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 1px 3px rgba(0,0,0,.4)",
                pointerEvents: "none",
              }}
            >
              {windowExecutionCount}
            </div>
          </OverlayView>
        )}

      {operacoesBadgeCount > 1 && lat != null && lng != null && (
        <OverlayView
          position={position}
          mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
          getPixelPositionOffset={getBadgeOffset}
        >
          <div
            style={{
              width: 20,
              height: 20,
              borderRadius: "50%",
              background: "#D32F2F",
              color: "#fff",
              fontSize: 11,
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 1px 3px rgba(0,0,0,.4)",
              pointerEvents: "none",
            }}
          >
            {operacoesBadgeCount}
          </div>
        </OverlayView>
      )}

      {showSemaforoOverlays && possuiOSAberta && ordemServico && (
        <OverlayView
          position={position}
          mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
        >
          <div
            style={{
              transform: `translate(-150%, -50%) scale(${zoomScale})`,
              pointerEvents: "none",
            }}
          >
            <IconOSNew width={30} height={35} heightSpec={3} />
          </div>
        </OverlayView>
      )}

      {showSemaforoOverlays &&
        justificativaAtraso &&
        possuiJustificativa &&
        statusSemaforo?.atrasado && (
          <OverlayView
            position={position}
            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
          >
            <div
              style={{
                transform: `translate(-150%, 10%) scale(${zoomScale})`,
                pointerEvents: "none",
              }}
            >
              <IoPricetag color="#FFFF01" size={35} />
            </div>
          </OverlayView>
        )}

      {showSemaforoOverlays &&
        !isExecutada &&
        statusSemaforo?.atrasado &&
        indicadoresDeDia?.has("diaEmAtraso") &&
        findedAtrasado?.QuantidadeDias < 0 && (
          <OverlayView
            position={position}
            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
          >
            <div
              style={{
                transform: `translate(50%, -50%) scale(${zoomScale})`,
                pointerEvents: "none",
              }}
            >
              <IconRepresentaDias
                numberDays={findedAtrasado.QuantidadeDias}
                color="#FF0000"
              />
            </div>
          </OverlayView>
        )}

      {showSemaforoOverlays &&
        !isExecutada &&
        statusSemaforo?.proximo &&
        indicadoresDeDia?.has("diaProximoVencimento") &&
        findedProximoVencimento?.QuantidadeDias !== undefined &&
        fillColor === "#FFA500" && (
          <OverlayView
            position={position}
            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
          >
            <div
              style={{
                transform: `translate(50%, -50%) scale(${zoomScale})`,
                pointerEvents: "none",
              }}
            >
              <IconRepresentaDias
                numberDays={findedProximoVencimento.QuantidadeDias}
                color="#FFA500"
              />
            </div>
          </OverlayView>
        )}
    </>
  );
}

export const ExecucaoTalhaoMarker = memo(ExecucaoTalhaoMarkerComponent);
export { getPlanoTalhaoDisponiveis };
